import { useFormikContext } from 'formik';
import React, { useEffect, useRef, useState } from 'react';

import styled from '@emotion/styled';

import { FormSubmitButton } from '../../../../components/Button/FormSubmitButton';
import DisabledCurrencyOnlyInputField from '../../../../components/FormCom/DisabledCurrencyOnlyInputField';
import { FormComponent } from '../../../../components/FormCom/FormComponent';
import PercentageOnlyInputField from '../../../../components/FormCom/PercentageOnlyInputField';
import WrappedNumberOnlyInputField from '../../../../components/FormCom/WrappedNumberOnlyInputField';
import AddIcon from '../../../../components/Icons/AddIcon';
import {
  BundlePriceInterface,
  BundlePriceSchema,
} from '../../../../models/dashboard/revenue/bundle-price';
import { createPriceManagementActions } from '../../../../redux/slices/priceManagementSlice';
import { useAppDisPatch, useAppSelector } from '../../../../redux/store';
import BundlesFormPresentation from './BundlesFormPresentation';
import DeleteBundleFormIconButton from './DeleteBundleFormIconButton';

/**
 * Lives inside a bundle's Formik context. Re-computes `originalPrice` whenever
 * `numberOfLookups` (Formik) or `effectivePricePerLookup` (prop from Redux) changes,
 * satisfying the requirement that Original Price updates immediately on either change.
 */

/**
 * Lives inside a bundle's Formik context. Re-computes `newPrice` whenever
 * `originalPrice` (set by OriginalPriceAutoSetter) or `discount` (user input) changes.
 * Formula: New Price = Original Price × (1 − Discount / 100)
 */
const NewPriceAutoSetter: React.FC = () => {
  const { setFieldValue, setFieldTouched, values } = useFormikContext<any>();

  useEffect(() => {
    const originalPrice = Number(values.originalPrice);
    const discount = Number(values.discount);
    const isDiscountValid =
      !Number.isNaN(discount) && discount >= 0 && discount <= 100;
    const computedNewPrice =
      originalPrice > 0 && isDiscountValid
        ? String(originalPrice * (1 - discount / 100))
        : '';
    setFieldValue('newPrice', computedNewPrice);
    setFieldTouched('newPrice', true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.originalPrice, values.discount]);

  return null;
};

interface OriginalPriceAutoSetterProps {
  effectivePricePerLookup: number;
}

const OriginalPriceAutoSetter: React.FC<OriginalPriceAutoSetterProps> = ({
  effectivePricePerLookup,
}) => {
  const { setFieldValue, setFieldTouched, values } = useFormikContext<any>();

  useEffect(() => {
    const numLookups = Number(values.numberOfLookups);
    const computedPrice =
      numLookups > 0 && effectivePricePerLookup > 0
        ? String(numLookups * effectivePricePerLookup)
        : '';
    setFieldValue('originalPrice', computedPrice);
    setFieldTouched('originalPrice', true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.numberOfLookups, effectivePricePerLookup]);

  return null;
};

const BundlesForm = () => {
  const dispatch = useAppDisPatch();

  const [openBundleForms, setOpenBundleForms] = useState<
    Record<string, boolean>
  >({});

  const { currentConfigData } = useAppSelector(
    (state) => state.currentConfigData,
  );

  const { pricingBundle, payAsYouGoPrice } = useAppSelector(
    (state) => state.priceManagementData,
  );

  const pricingBundleSize = pricingBundle.length;

  // Use the newly entered price if it's a valid positive whole number;
  // otherwise fall back to the previously saved unit price.
  const payAsYouGoPriceNum = Number(payAsYouGoPrice);
  const isNewPriceEnteredAndValid =
    !!payAsYouGoPrice &&
    payAsYouGoPriceNum > 0 &&
    Number.isInteger(payAsYouGoPriceNum);
  const effectivePricePerLookup = isNewPriceEnteredAndValid
    ? payAsYouGoPriceNum
    : (currentConfigData?.unitPrice ?? 0);

  const handleBundlePricing = (values: BundlePriceInterface, id: string) => {
    dispatch(
      createPriceManagementActions.updatePricingBundlesAtId({
        id,
        pricingBundle: values,
      }),
    );
    dispatch(createPriceManagementActions.addEmptyPricingBundle());
  };

  const handleBundleFieldChange = (
    id: string,
    field: keyof BundlePriceInterface,
    value: string,
  ) => {
    const currentBundle = pricingBundle.find((bundle) => bundle.id === id);

    if (!currentBundle) {
      return;
    }

    dispatch(
      createPriceManagementActions.updatePricingBundlesAtId({
        id,
        pricingBundle: {
          numberOfLookups: currentBundle.numberOfLookups,
          originalPrice: currentBundle.originalPrice,
          newPrice: currentBundle.newPrice,
          discount: currentBundle.discount,
          [field]: value,
        },
      }),
    );
  };

  const handleToggleBundleForm = (id: string) => {
    setOpenBundleForms((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const bundleDataFromApi = currentConfigData?.pricingBundle;

  // Seed (or re-seed) the Redux bundle list whenever a fresh API response arrives.
  // Using a ref that stores the last array reference means we reinitialize on the
  // initial load AND after a successful save (reset + re-fetch), but skip ordinary
  // re-renders where bundleDataFromApi hasn't changed.
  const lastInitializedBundleDataRef =
    useRef<typeof bundleDataFromApi>(undefined);
  useEffect(() => {
    if (!bundleDataFromApi || bundleDataFromApi.length === 0) return;
    if (bundleDataFromApi === lastInitializedBundleDataRef.current) return;

    lastInitializedBundleDataRef.current = bundleDataFromApi;
    dispatch(
      createPriceManagementActions.initializePricingBundlesFromApi(
        bundleDataFromApi.map((b) => ({
          numberOfLookups: String(b.numberOfLookups),
          originalPrice: String(b.originalPrice),
          newPrice: String(b.newPrice),
          discount: String(b.discount),
        })),
      ),
    );
    // eslint-disable-next-line
  }, [bundleDataFromApi]);

  return (
    <Container>
      <FormArea>
        {pricingBundle.map((pricingBundleItem, index) => {
          const bundleInitialValues: BundlePriceInterface = {
            numberOfLookups: pricingBundleItem.numberOfLookups,
            originalPrice: pricingBundleItem.originalPrice,
            newPrice: pricingBundleItem.newPrice,
            discount: pricingBundleItem.discount,
          };

          return (
            <FormComponent
              key={pricingBundleItem.id}
              initialValues={bundleInitialValues}
              schema={BundlePriceSchema}
              onSubmit={(values: BundlePriceInterface) =>
                handleBundlePricing(values, pricingBundleItem.id)
              }
            >
              <LabelArea>
                <Label>Bundles ({index + 1}/5)</Label>

                {pricingBundleSize !== 5
                  ? pricingBundleSize === index + 1 && (
                      <AddBundleBtn>
                        <AddIcon /> Add bundle
                      </AddBundleBtn>
                    )
                  : null}
              </LabelArea>

              <BundleFormArea>
                <BundleFormHeader>
                  <BundleText>
                    <span>{index + 1}</span> Bundle {index + 1}
                  </BundleText>

                  {/*  */}
                  <DeleteBundleFormIconButton
                    pricingBundleItem={pricingBundleItem}
                    onToggle={() =>
                      handleToggleBundleForm(pricingBundleItem.id)
                    }
                  />
                </BundleFormHeader>

                <HideableBundleFormArea
                  className={
                    openBundleForms[pricingBundleItem.id] ? 'hidden' : ''
                  }
                >
                  <FormRow>
                    <WrappedNumberOnlyInputField
                      // showErrorLabel={false}
                      label='Number of Lookups'
                      name='numberOfLookups'
                      id='numberOfLookups'
                      type='text'
                      placeholder='Number of Lookups'
                      onValueChange={(value) =>
                        handleBundleFieldChange(
                          pricingBundleItem.id,
                          'numberOfLookups',
                          value,
                        )
                      }
                    />

                    {/* Auto-computes originalPrice from numberOfLookups × effectivePricePerLookup */}
                    <OriginalPriceAutoSetter
                      effectivePricePerLookup={effectivePricePerLookup}
                    />

                    <DisabledCurrencyOnlyInputField
                      showErrorLabel={false}
                      label='Original price (₦)'
                      name='originalPrice'
                      id='originalPrice'
                      type='text'
                      // placeholder='Original Price'
                      onValueChange={(value) =>
                        handleBundleFieldChange(
                          pricingBundleItem.id,
                          'originalPrice',
                          value,
                        )
                      }
                      readonly={true}
                    />
                  </FormRow>

                  <FormRow>
                    {/* Auto-computes newPrice = originalPrice × (1 − discount / 100) */}
                    <NewPriceAutoSetter />

                    <PercentageOnlyInputField
                      label='Discount (%)'
                      name='discount'
                      id='discount'
                      type='text'
                      placeholder='Discount'
                      allowHype
                      onValueChange={(value) =>
                        handleBundleFieldChange(
                          pricingBundleItem.id,
                          'discount',
                          value,
                        )
                      }
                    />

                    <DisabledCurrencyOnlyInputField
                      showErrorLabel={false}
                      label='New price (₦)'
                      name='newPrice'
                      id='newPrice'
                      type='text'
                      // placeholder='Auto-calculated'
                      readonly={true}
                    />
                  </FormRow>
                </HideableBundleFormArea>
                <BundlesFormPresentation />
              </BundleFormArea>
            </FormComponent>
          );
        })}
      </FormArea>
    </Container>
  );
};

export default BundlesForm;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const LabelArea = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Label = styled.div`
  font-weight: 600;
  font-size: 14px;
  line-height: 14px;
  color: #5c5963;
`;

const FormArea = styled.div`
  display: flex;
  flex-direction: column-reverse;
  gap: 16px;
  width: 100%;
`;

const AddBundleBtn = styled(FormSubmitButton)`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 14px;
  line-height: 14px;
  color: white;
  cursor: pointer;
  padding: 12px 16px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  background-color: ${({ theme }) => theme.palette.blueColor};
`;

const BundleFormArea = styled.div`
  border: 1px solid ${({ theme }) => theme.palette.borderColor};
  border-radius: 12px;
  width: 100%;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 32px;
`;

const HideableBundleFormArea = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;

  &.hidden {
    display: none;
  }
`;

const BundleFormHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  width: 100%;
`;

const BundleText = styled.div`
  font-weight: 600;
  font-size: 14px;
  line-height: 14px;
  display: flex;
  color: #5c5963;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;

  span {
    font-weight: 700;
    color: white;
    background-color: ${({ theme }) => theme.palette.blueColor};
    border-radius: 50%;
    width: 27px;
    height: 27px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
`;
